import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  GalleryItem,
  PREVIEW_FORMATS,
  isPastelUri,
  parsePastelUri,
  CONTENT_CATEGORIES,
  Template,
  TemplateSummary,
} from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import {
  renderCompositeThumbnail,
  renderCompositeAsBlob,
} from '../utils/canvasRenderer';

/* ================================================================
   STYLED COMPONENTS
   ================================================================ */

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const ItemCount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const GalleryCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }

  &:hover .card-actions {
    opacity: 1;
  }
`;

const GalleryImage = styled.div<{ $aspectRatio: number }>`
  width: 100%;
  aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
  background: ${({ theme }) => theme.colors.background};
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PastelBackground = styled.div<{ $color: string }>`
  width: 100%;
  height: 100%;
  background-color: ${({ $color }) => $color};
`;

const CardActions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 10;
`;

const ActionButton = styled.button<{ $danger?: boolean }>`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.15s ease;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    background-color: ${({ $danger, theme }) =>
      $danger ? theme.colors.error : theme.colors.primary};
    color: white;
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const GalleryInfo = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  min-height: 28px;
`;

const GalleryName = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const NameInput = styled.input`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: 2px 6px;
  flex: 1;
  min-width: 0;
  outline: none;
`;

const GalleryMeta = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Badge = styled.span<{ $color?: string }>`
  background: ${({ $color, theme }) => $color || theme.colors.primary};
  color: white;
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const GalleryContent = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xxl};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px dashed ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background-color: ${({ theme }) => theme.colors.surface};

  p {
    margin: ${({ theme }) => theme.spacing.xs} 0;
  }
`;

const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
`;

const SkeletonCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: ${({ theme }) => theme.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const SkeletonImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background} 25%,
    ${({ theme }) => theme.colors.surfaceHover} 50%,
    ${({ theme }) => theme.colors.background} 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
`;

const SkeletonText = styled.div<{ $width?: string }>`
  height: 14px;
  width: ${({ $width }) => $width || '80%'};
  margin: 8px 12px;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.background} 25%,
    ${({ theme }) => theme.colors.surfaceHover} 50%,
    ${({ theme }) => theme.colors.background} 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
`;

/* ================================================================
   CONSTANTS
   ================================================================ */

const NETWORK_COLORS: Record<string, string> = {
  instagram: '#E4405F',
  facebook: '#1877F2',
  twitter: '#1DA1F2',
  linkedin: '#0A66C2',
  tiktok: '#000000',
  threads: '#000000',
  gbp: '#4285F4',
  bluesky: '#1185fe',
};

/* ================================================================
   COMPONENT
   ================================================================ */

export function GalleryPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [templateCache, setTemplateCache] = useState<Record<string, Template>>({});
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [workspaceLogoUrl, setWorkspaceLogoUrl] = useState<string | undefined>();
  const [brandColors, setBrandColors] = useState<string[]>([]);
  const [brandFonts, setBrandFonts] = useState<string[]>([]);

  // ==================== Data fetching ====================

  useEffect(() => {
    if (workspaceId) {
      fetchGalleryItems();
      fetchTemplates();
      fetchWorkspaceLogo();
      fetchBrandInfo();
    }
  }, [workspaceId]);

  const fetchWorkspaceLogo = async () => {
    try {
      const data = await apiClient.get<{ logoUrl: string | null }>(`/api/workspaces/${workspaceId}/logo`);
      if (data.logoUrl) setWorkspaceLogoUrl(data.logoUrl);
    } catch { /* no logo */ }
  };

  const fetchBrandInfo = async () => {
    try {
      const data = await apiClient.get<{ brandColors?: string[]; brandFonts?: string[] }>(`/api/workspaces/${workspaceId}/brand-info`);
      if (data.brandColors?.length) setBrandColors(data.brandColors);
      if (data.brandFonts?.length) setBrandFonts(data.brandFonts);
    } catch { /* no brand info */ }
  };

  const fetchGalleryItems = async () => {
    try {
      const data = await apiClient.get<GalleryItem[]>(`/api/workspaces/${workspaceId}/gallery`);
      setGalleryItems(data);
    } catch (error) {
      console.error('Failed to fetch gallery items:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await apiClient.get<TemplateSummary[]>(`/api/workspaces/${workspaceId}/templates`);
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const getFullTemplate = useCallback(async (templateId: string): Promise<Template | null> => {
    if (templateCache[templateId]) return templateCache[templateId];
    try {
      const template = await apiClient.get<Template>(`/api/workspaces/${workspaceId}/templates/${templateId}`);
      setTemplateCache(prev => ({ ...prev, [templateId]: template }));
      return template;
    } catch {
      return null;
    }
  }, [workspaceId, templateCache]);

  // ==================== Thumbnail generation ====================

  const buildRenderOptions = useCallback((item: GalleryItem, template: Template) => {
    const dynamicTexts = item.dynamicTexts
      ? Object.fromEntries(
          template.elements
            .filter(el => el.elementType === 'text' && 'dynamicType' in el && (el as { isDynamic?: boolean }).isDynamic)
            .map(el => {
              const textEl = el as { id: string; dynamicType?: string };
              const dynamicType = textEl.dynamicType as keyof NonNullable<typeof item.dynamicTexts>;
              return [el.id, item.dynamicTexts?.[dynamicType] || ''];
            })
        )
      : undefined;

    return {
      dynamicTexts,
      backgroundOffsetX: item.backgroundOffsetX,
      backgroundOffsetY: item.backgroundOffsetY,
      backgroundScale: item.backgroundScale ?? 1.0,
      workspaceLogoUrl,
      brandColors: brandColors.length ? brandColors : undefined,
      brandFonts: brandFonts.length ? brandFonts : undefined,
      colorMode: template.colorMode,
    };
  }, [workspaceLogoUrl, brandColors, brandFonts]);

  const generateThumbnails = useCallback(async () => {
    const itemsWithTemplates = galleryItems.filter(item => item.templateId);
    if (itemsWithTemplates.length === 0) return;

    const newThumbnails: Record<string, string> = {};

    for (const item of itemsWithTemplates) {
      if (!item.templateId) continue;
      try {
        const template = await getFullTemplate(item.templateId);
        if (!template) continue;

        const thumbnailUrl = await renderCompositeThumbnail(
          item.imageUrl,
          template,
          400,
          buildRenderOptions(item, template),
        );
        if (thumbnailUrl) {
          newThumbnails[item.id] = thumbnailUrl;
        }
      } catch (error) {
        console.error(`Failed to generate thumbnail for gallery item ${item.id}:`, error);
      }
    }

    setThumbnails(prev => ({ ...prev, ...newThumbnails }));
  }, [galleryItems, getFullTemplate, buildRenderOptions]);

  useEffect(() => {
    if (galleryItems.length > 0 && templates.length > 0) {
      generateThumbnails();
    }
  }, [galleryItems, templates, generateThumbnails, workspaceLogoUrl]);

  // ==================== Actions ====================

  const handleDelete = async (itemId: string) => {
    if (!confirm('Delete this saved design?')) return;
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/gallery/${itemId}`);
      setGalleryItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Failed to delete gallery item:', error);
    }
  };

  const handleStartRename = (item: GalleryItem) => {
    setEditingId(item.id);
    setEditingName(item.name || `${item.network} - ${PREVIEW_FORMATS[item.format].label}`);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  const handleSaveRename = async () => {
    if (!editingId || !editingName.trim()) {
      setEditingId(null);
      return;
    }
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/gallery/${editingId}`, {
        name: editingName.trim(),
      });
      setGalleryItems(prev =>
        prev.map(item =>
          item.id === editingId ? { ...item, name: editingName.trim() } : item
        )
      );
    } catch (error) {
      console.error('Failed to rename gallery item:', error);
    } finally {
      setEditingId(null);
    }
  };

  const handleDownload = async (item: GalleryItem) => {
    setDownloadingId(item.id);
    try {
      // If item has a template, render the full composite at full resolution
      if (item.templateId) {
        const template = await getFullTemplate(item.templateId);
        if (template) {
          const blob = await renderCompositeAsBlob(
            item.imageUrl,
            template,
            buildRenderOptions(item, template),
          );
          if (blob) {
            triggerDownload(blob, `${item.name || item.network}-gallery.png`);
            return;
          }
        }
      }

      // Fallback: download the raw image
      const response = await fetch(
        `/api/proxy/image?url=${encodeURIComponent(item.imageUrl)}`
      );
      const blob = await response.blob();
      triggerDownload(blob, `${item.name || item.network}-gallery.png`);
    } catch (error) {
      console.error('Failed to download gallery item:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyContent = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  // ==================== Render ====================

  if (isLoading) {
    return (
      <>
        <PageHeader>
          <div>
            <Title>Gallery</Title>
            <Subtitle>Your saved favorite designs</Subtitle>
          </div>
        </PageHeader>
        <SkeletonGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i}>
              <SkeletonImage />
              <SkeletonText $width="60%" />
              <SkeletonText $width="40%" />
              <SkeletonText $width="90%" />
            </SkeletonCard>
          ))}
        </SkeletonGrid>
      </>
    );
  }

  return (
    <>
      <PageHeader>
        <div>
          <HeaderLeft>
            <Title>Gallery</Title>
            {galleryItems.length > 0 && (
              <ItemCount>{galleryItems.length} design{galleryItems.length !== 1 ? 's' : ''}</ItemCount>
            )}
          </HeaderLeft>
          <Subtitle>Your saved favorite designs</Subtitle>
        </div>
      </PageHeader>

      {galleryItems.length === 0 ? (
        <EmptyState>
          <p style={{ fontSize: '2rem' }}>🖼️</p>
          <p><strong>No saved designs yet</strong></p>
          <p>Go to Generate, preview a post, and click "Save to Gallery" to keep your best ads here.</p>
        </EmptyState>
      ) : (
        <GalleryGrid>
          {galleryItems.map((item) => (
            <GalleryCard key={item.id}>
              <CardActions className="card-actions">
                <ActionButton
                  title="Download"
                  onClick={() => handleDownload(item)}
                  disabled={downloadingId === item.id}
                >
                  {downloadingId === item.id ? '...' : '↓'}
                </ActionButton>
                <ActionButton
                  title="Copy text"
                  onClick={() => handleCopyContent(item.content)}
                >
                  📋
                </ActionButton>
                <ActionButton
                  $danger
                  title="Delete"
                  onClick={() => handleDelete(item.id)}
                >
                  ×
                </ActionButton>
              </CardActions>

              <GalleryImage $aspectRatio={PREVIEW_FORMATS[item.format].aspectRatio}>
                {thumbnails[item.id] ? (
                  <img src={thumbnails[item.id]} alt={item.name || 'Saved design'} />
                ) : isPastelUri(item.imageUrl) ? (
                  <PastelBackground $color={parsePastelUri(item.imageUrl) || '#F0F0F0'} />
                ) : (
                  <img src={item.imageUrl} alt={item.name || 'Saved design'} />
                )}
              </GalleryImage>

              <GalleryInfo>
                <NameRow>
                  {editingId === item.id ? (
                    <NameInput
                      ref={nameInputRef}
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onBlur={handleSaveRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveRename();
                        if (e.key === 'Escape') setEditingId(null);
                      }}
                      autoFocus
                    />
                  ) : (
                    <GalleryName
                      onClick={() => handleStartRename(item)}
                      title="Click to rename"
                    >
                      {item.name || `${item.network} - ${PREVIEW_FORMATS[item.format].label}`}
                    </GalleryName>
                  )}
                </NameRow>
                <GalleryMeta>
                  <Badge $color={NETWORK_COLORS[item.network]}>
                    {item.network}
                  </Badge>
                  {item.contentCategory && (
                    <Badge>
                      {CONTENT_CATEGORIES[item.contentCategory]?.label || item.contentCategory}
                    </Badge>
                  )}
                  <Badge $color="#888">
                    {PREVIEW_FORMATS[item.format].label}
                  </Badge>
                </GalleryMeta>
                <GalleryContent>{item.content}</GalleryContent>
              </GalleryInfo>
            </GalleryCard>
          ))}
        </GalleryGrid>
      )}
    </>
  );
}
